/**
 * F-16 copy/paste of a CELL RANGE between Kola boards (2026-10) — PURE logic
 * (no React, no DOM): what the grid puts on the clipboard beside its TSV, what
 * it reads back, and where the copied block's contained images land.
 *
 * The carrier is a SECOND clipboard flavor, `CELL_CLIPBOARD_MIME`, written in
 * the same `copy` event as the `text/plain` TSV. Sheets and Excel only ever
 * read the TSV, so the interop F-16 was built on is untouched; a Kola grid
 * reads the Kola flavor first and falls back to the TSV when it is absent
 * (text copied anywhere else, or a browser that drops custom flavors — the
 * TSV then pastes exactly as it did before this flavor existed).
 *
 * The payload is POSITIONAL — a matrix relative to the copied range's
 * top-left, no row or column ids — which is what lets it land on a different
 * board, a different sheet, or a different place on the same grid: the paste
 * puts the matrix's top-left on the selected cell and the rest follows.
 *
 * Contained elements (the images that live IN cells — F-30/F-36) travel with
 * their cells, keyed by the cell's offset in the block. Their `assetId`s are
 * served by `GET /api/assets/:id`, which authorizes through the asset's OWN
 * board rather than the board that displays it (api/assets.ts), so a copy on
 * another board renders — but it shares the source's bytes, and a future
 * per-board ACL (F-101) or orphan sweep would have to copy assets instead.
 */
import {
  type Anchor,
  type BoardDoc,
  cellKey,
  type ElementPayload,
  type ElementType,
} from "@kola/shared";

import { type CopiedCell, copiedCellOf, sanitizeCopiedCell } from "../../core/cell-copy.js";
import type { ElementDuplicate } from "../../core/session.js";

/**
 * Clipboard flavor name. The version in the payload (not the name) is what
 * an older client checks: a payload it cannot honor parses as null, and the
 * paste falls through to the TSV flavor — the correct degradation.
 */
export const CELL_CLIPBOARD_MIME = "application/x-kola-cells";
export const CELL_CLIPBOARD_KIND = "kola/cells";
export const CELL_CLIPBOARD_VERSION = 1;

/** One contained element of the copied block, by the cell it sits in. */
export interface CopiedElement {
  /** Row / column of its cell, counted from the block's top-left. */
  dr: number;
  dc: number;
  type: ElementType;
  w: number;
  h: number;
  rotation: number;
  /** Board-unit offset from the cell's top-left (`Anchor` cell kind). */
  dx: number;
  dy: number;
  payload: ElementPayload;
}

export interface CellClipboard {
  kind: typeof CELL_CLIPBOARD_KIND;
  version: typeof CELL_CLIPBOARD_VERSION;
  /** Rectangular: every row has the block's full width. */
  rows: (CopiedCell | null)[][];
  elements: CopiedElement[];
}

/**
 * Snapshot the block `rowIds × colIds` (the selected range, in grid order).
 * Elements are indexed in ONE pass over the board rather than one
 * `elementsForCell` scan per cell — a range copy can be thousands of cells on
 * a board with hundreds of images.
 */
export function buildCellClipboard(
  board: BoardDoc,
  sheetId: string,
  rowIds: readonly string[],
  colIds: readonly string[],
): CellClipboard {
  const rows = rowIds.map((rowId) =>
    colIds.map((colId) => copiedCellOf(board.getCell(sheetId, rowId, colId))),
  );
  const offsetByCell = new Map<string, { dr: number; dc: number }>();
  rowIds.forEach((rowId, dr) => {
    colIds.forEach((colId, dc) => offsetByCell.set(cellKey(rowId, colId), { dr, dc }));
  });
  const elements: CopiedElement[] = [];
  // Lowest z first: the paste stacks duplicates in list order, so the copies
  // overlap the way the originals did.
  const contained = board
    .listElements()
    .filter((el) => el.anchor.kind === "cell")
    .sort((a, b) => a.z - b.z);
  for (const el of contained) {
    if (el.anchor.kind !== "cell") continue; // narrows — already filtered
    const at = offsetByCell.get(cellKey(el.anchor.rowId, el.anchor.colId));
    if (at === undefined) continue;
    elements.push({
      dr: at.dr,
      dc: at.dc,
      type: el.type,
      w: el.w,
      h: el.h,
      rotation: el.rotation,
      dx: el.anchor.dx,
      dy: el.anchor.dy,
      payload: el.payload,
    });
  }
  return { kind: CELL_CLIPBOARD_KIND, version: CELL_CLIPBOARD_VERSION, rows, elements };
}

export function serializeCellClipboard(clipboard: CellClipboard): string {
  return JSON.stringify(clipboard);
}

/**
 * Read the Kola flavor back, or null for anything that is not a block this
 * client can honor. Never throws — the clipboard is user-controlled input and
 * a paste that cannot be parsed must fall through to the TSV path.
 */
export function parseCellClipboard(text: string): CellClipboard | null {
  if (text.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const value = parsed as { kind?: unknown; version?: unknown; rows?: unknown; elements?: unknown };
  if (value.kind !== CELL_CLIPBOARD_KIND || value.version !== CELL_CLIPBOARD_VERSION) return null;
  if (!Array.isArray(value.rows) || value.rows.length === 0) return null;
  const rawRows = (value.rows as unknown[]).filter((row): row is unknown[] => Array.isArray(row));
  if (rawRows.length === 0) return null;
  const width = rawRows.reduce((max, row) => Math.max(max, row.length), 0);
  if (width === 0) return null;
  const rows = rawRows.map((row) => {
    const cells: (CopiedCell | null)[] = [];
    for (let dc = 0; dc < width; dc++) cells.push(sanitizeCopiedCell(row[dc]));
    return cells;
  });
  const elements = (Array.isArray(value.elements) ? value.elements : []).filter(
    (el): el is CopiedElement => isCopiedElement(el, rows.length, width),
  );
  return { kind: CELL_CLIPBOARD_KIND, version: CELL_CLIPBOARD_VERSION, rows, elements };
}

function isCopiedElement(value: unknown, height: number, width: number): boolean {
  if (typeof value !== "object" || value === null) return false;
  const el = value as Partial<CopiedElement>;
  return (
    Number.isInteger(el.dr) &&
    Number.isInteger(el.dc) &&
    (el.dr as number) >= 0 &&
    (el.dr as number) < height &&
    (el.dc as number) >= 0 &&
    (el.dc as number) < width &&
    typeof el.type === "string" &&
    isFiniteNumber(el.w) &&
    isFiniteNumber(el.h) &&
    isFiniteNumber(el.rotation) &&
    isFiniteNumber(el.dx) &&
    isFiniteNumber(el.dy) &&
    typeof el.payload === "object" &&
    el.payload !== null
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Where a block lands: the target cell at each offset, with its board top-left. */
export type PasteCellLocator = (
  dr: number,
  dc: number,
) => { rowId: string; colId: string; x: number; y: number } | null;

/**
 * The copied block's contained elements as duplicates INTO the target cells —
 * the same cell offset (`dx`/`dy`) they had in their source cells, so a
 * storyboard frame pasted onto another board sits in its cell exactly as it
 * did. `x`/`y` is the fallback board position every cell-anchored element
 * caches (resolved from the target cell's top-left). An element whose target
 * cell is missing is skipped rather than degraded to a floating one: it was
 * copied AS cell content, and a cell that is not there has no content.
 */
export function copiedElementDuplicates(
  elements: readonly CopiedElement[],
  locate: PasteCellLocator,
): ElementDuplicate[] {
  const out: ElementDuplicate[] = [];
  elements.forEach((el, index) => {
    const cell = locate(el.dr, el.dc);
    if (cell === null) return;
    const anchor: Anchor = { kind: "cell", rowId: cell.rowId, colId: cell.colId, dx: el.dx, dy: el.dy };
    out.push({
      ref: `copied:${index}`,
      type: el.type,
      x: Math.round(cell.x + el.dx),
      y: Math.round(cell.y + el.dy),
      w: el.w,
      h: el.h,
      rotation: el.rotation,
      anchor,
      payload: el.payload,
    });
  });
  return out;
}
